import React from 'react'
import moment from 'moment'
import { BiSupport } from 'react-icons/bi'
import { FaCalendarCheck, FaCheck, FaFolderOpen, FaUser } from 'react-icons/fa'
import LoadingButton from './LoadingButton'

/* A notification addressed to one user (recipient_id set: a leave status
   change) leads with what happened; an inbox one (an application, a ticket)
   leads with who did it. */
export const notificationTitle = (item) =>
  item?.recipient_id ? item.title : item?.user?.name || item?.title

export const isUnread = (item) => String(item?.isRead) !== '1'

/* ============================================================================
   Notification list.

   Admin: two tabs (2 = Support, 3 = Leaves) over the shared inbox plus
   anything addressed to the admin. Everyone else: one list, no tabs, of their
   own leave status changes and the projects they were added to. Opening a row is the parent's `onOpen`
   (mark read + navigate); markAsReadHandler is the only thing that writes.
   ========================================================================== */
const NotificationComponent = ({
  notificationData,
  isAdmin,
  isActive,
  markAsReadHandler,
  onOpen,
}) => {
  const supportData = notificationData.filter(
    (item) => item.type === 'support',
  )
  const leaveDetails = notificationData.filter(
    (item) => item.type === 'leave',
  )

  const Row = ({ item, kind, title, description, onOpen }) => (
    <div
      className={'notif-item' + (isUnread(item) ? ' is-unread' : '')}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen()
      }}
    >
      <span className={'notif-icon notif-icon-' + kind}>
        {kind === 'support' ? (
          <BiSupport />
        ) : kind === 'project' ? (
          <FaFolderOpen />
        ) : item?.recipient_id ? (
          <FaCalendarCheck />
        ) : (
          <FaUser />
        )}
      </span>

      <span className="notif-body">
        <span className="notif-title">{title || '—'}</span>
        {description ? (
          <span className="notif-desc">{description}</span>
        ) : null}
        <span className="notif-time">
          {moment.unix(item?.created_at).fromNow()}
        </span>
      </span>

      <span className="notif-actions">
        {isUnread(item) ? <span className="notif-dot" aria-hidden="true" /> : null}
        <LoadingButton
          iconOnly
          className="lb-bare notif-read"
          title="Mark as read"
          ariaLabel="Mark as read"
          successLabel="Read"
          errorLabel="Failed"
          onClick={(e) => e.stopPropagation()}
          onAction={() => markAsReadHandler(item.id)}
        >
          <FaCheck />
        </LoadingButton>
      </span>
    </div>
  )

  const Empty = ({ label }) => (
    <div className="notif-empty">
      <span className="notif-empty-title">{label}</span>
      <span className="notif-empty-sub">You&rsquo;re all caught up.</span>
    </div>
  )

  if (notificationData.length === 0) {
    return (
      <div className="notif-list">
        <Empty label="No notifications" />
      </div>
    )
  }

  // kind = null: each row takes its own type (the employee list mixes them)
  const rows = (list, kind, emptyLabel) =>
    list.length > 0 ? (
      list.map((item, index) => (
        <Row
          key={item.id ?? index}
          item={item}
          kind={kind || item.type}
          title={notificationTitle(item)}
          description={item?.description}
          onOpen={() => onOpen(item)}
        />
      ))
    ) : (
      <Empty label={emptyLabel} />
    )

  if (!isAdmin) {
    return (
      <div className="notif-list">
        {rows(
          notificationData.filter((item) => item.type === 'leave' || item.type === 'project'),
          null,
          'No updates',
        )}
      </div>
    )
  }

  return (
    <div className="notif-list">
      {isActive === 2 && rows(supportData, 'support', 'No support notifications')}
      {isActive === 3 && rows(leaveDetails, 'leave', 'No leave notifications')}
    </div>
  )
}

export default NotificationComponent
